import React, { useMemo } from 'react';
import { Card, Stack, Text, Grid, Group, Badge, Progress, Paper, Box, Tabs } from '@mantine/core';
import { IconTrendingUp, IconShield, IconAlertTriangle, IconShieldCheck, IconChartBar } from '@tabler/icons-react';
import ResidualRiskCards from '../components/ResidualRiskCards';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// Risk priority colors consistent with RiskLandscape.js
const riskPriorityColors = {
  Critical: '#1A1A1A',    // Black
  High: '#993333',        // Red
  Significant: '#B94D4D', // Amber
  Moderate: '#D4AA7D',    // Light Orange
  Low: '#B8C2B7',         // Blue
  Negligible: '#D8D8D8',  // Green
  None: '#EBEBEB'         // Light Grey
};

const areaColors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f', '#455a64', '#00796b'];

function toTitleCase(str = '') {
  return String(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w|\s\w/g, (m) => m.toUpperCase());
}

// Helper function to normalize priority capitalization for color mapping
const normalizePriority = (priority) => {
  if (!priority) return 'None';
  const normalized = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
  return normalized;
};

export default function ChartsSummaryV2({ riskFormData, residualRiskData, savedControls, modelId }) {
  const allSectionRisks = useMemo(() => {
    const aggregated = {};
    Object.entries(riskFormData || {}).forEach(([section, data]) => {
      aggregated[section] = {};
      Object.entries(data.areaRisks || {}).forEach(([category, risks]) => {
        aggregated[section][category] = Array.isArray(risks) ? risks : [];
      });
    });
    return aggregated;
  }, [riskFormData]);

  // Enhanced risk priority analysis
  const riskAnalysis = useMemo(() => {
    const priorityCounts = {
      Critical: 0,
      High: 0,
      Significant: 0,
      Moderate: 0,
      Low: 0,
      Negligible: 0,
      None: 0,
    };
    
    const sectionBreakdown = {};
    let totalRisks = 0;

    Object.entries(allSectionRisks).forEach(([section, categories]) => {
      sectionBreakdown[section] = {
        Critical: 0,
        High: 0,
        Significant: 0,
        Moderate: 0,
        Low: 0,
        Negligible: 0,
        None: 0,
        total: 0
      };

      Object.values(categories).forEach((risks) => {
        (risks || []).forEach((risk) => {
          const priority = normalizePriority(risk.priority || 'None');
          priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
          sectionBreakdown[section][priority] = (sectionBreakdown[section][priority] || 0) + 1;
          sectionBreakdown[section].total += 1;
          totalRisks += 1;
        });
      });
    });

    return { priorityCounts, sectionBreakdown, totalRisks };
  }, [allSectionRisks]);

  const priorityChartData = useMemo(() => {
    return Object.entries(riskAnalysis.priorityCounts)
      .filter(([_, count]) => count > 0)
      .map(([name, value]) => ({
        name,
        value,
        fill: riskPriorityColors[name] || '#CCCCCC'
      }));
  }, [riskAnalysis.priorityCounts]);

  const sectionDistributionData = useMemo(() => {
    return Object.entries(riskAnalysis.sectionBreakdown)
      .filter(([_, data]) => data.total > 0)
      .map(([name, data], index) => ({
        name: toTitleCase(name),
        value: data.total,
        critical: data.Critical,
        high: data.High,
        fill: areaColors[index % areaColors.length]
      }));
  }, [riskAnalysis.sectionBreakdown]);

  // Get all risks with residual assessments (copied from AutopilotAssessmentFlow)
  const risksWithResidual = useMemo(() => {
    const results = [];
    
    Object.entries(riskFormData || {}).forEach(([areaName, areaData]) => {
      const areaRisks = areaData?.areaRisks || {};
      Object.entries(areaRisks).forEach(([sourceName, risks]) => {
        if (Array.isArray(risks)) {
          risks.forEach(risk => {
            if (risk.status === 'Saved' && residualRiskData?.[risk.riskId]) {
              const residualData = residualRiskData[risk.riskId];
              const controls = savedControls?.[risk.riskId] || [];
              results.push({
                ...risk,
                areaName,
                sourceName,
                controls: controls.length,
                residualData
              });
            }
          });
        }
      });
    });
    
    return results;
  }, [riskFormData, residualRiskData, savedControls]);


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper p="sm" shadow="md" withBorder>
          <Text fw={600} size="sm">{label}</Text>
          {payload.map((entry, index) => (
            <Text key={index} c={entry.color} size="sm">
              {entry.name}: {entry.value}
            </Text>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Stack gap="lg">
      {/* Key Metrics Row */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconAlertTriangle size={20} style={{ color: riskPriorityColors.Critical }} />
                <Text fw={700} size="xl" style={{ color: riskPriorityColors.Critical }}>
                  {riskAnalysis.priorityCounts.Critical}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">Critical Risks</Text>
              <Text size="xs" ta="center" style={{ color: riskPriorityColors.Critical }}>Immediate attention required</Text>
            </Stack>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconTrendingUp size={20} style={{ color: riskPriorityColors.High }} />
                <Text fw={700} size="xl" style={{ color: riskPriorityColors.High }}>
                  {riskAnalysis.priorityCounts.High}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">High Priority</Text>
              <Text size="xs" ta="center" style={{ color: riskPriorityColors.High }}>Plan mitigation soon</Text>
            </Stack>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconShield size={20} color="#339af0" />
                <Text fw={700} size="xl" c="blue">
                  {riskAnalysis.totalRisks}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">Total Risks</Text>
              <Text size="xs" ta="center" c="dimmed">Across all areas</Text>
            </Stack>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder radius="md" p="md" h={120}>
            <Stack gap="xs" align="center" justify="center" h="100%">
              <Group gap="xs" align="center">
                <IconShieldCheck size={20} color="#2f9e44" />
                <Text fw={700} size="xl" c="green">
                  {Object.values(savedControls || {}).reduce((total, controls) => total + (controls?.length || 0), 0)}
                </Text>
              </Group>
              <Text size="sm" ta="center" c="dimmed">Actions</Text>
              <Text size="xs" ta="center" c="dimmed">Planned controls</Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Charts Row */}
      <Grid>
        {/* Risk Priority Distribution */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" p="lg" h={400}>
            <Stack gap="md" h="100%">
              <Group justify="space-between" align="center">
                <Text fw={600} size="lg">Risks by Priority</Text>
                <Badge variant="light" color="blue" size="sm">
                  {priorityChartData.length} priority levels
                </Badge>
              </Group>
              <div style={{ flex: 1, minHeight: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={priorityChartData} margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      interval={0} 
                      height={60}
                      fontSize={12}
                    />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {priorityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Risk Areas Breakdown */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder radius="md" p="lg" h={400}>
            <Stack gap="md" h="100%">
              <Group justify="space-between" align="center">
                <Text fw={600} size="lg">Risks by Area</Text>
                <Badge variant="light" color="green" size="sm">
                  {sectionDistributionData.length} areas
                </Badge>
              </Group>
              <div style={{ flex: 1, minHeight: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sectionDistributionData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={90}
                      innerRadius={40}
                      paddingAngle={2}
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                      fontSize={11}
                    >
                      {sectionDistributionData.map((entry, index) => (
                        <Cell key={`cell-pie-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend
                      iconSize={8}
                      wrapperStyle={{ fontSize: '10px', paddingTop: '5px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Tabbed Panel: Residual Risk Summary & Area Progress */}
      <Tabs defaultValue="residual" variant="default">
        <Tabs.List>
          <Tabs.Tab value="residual" leftSection={<IconShieldCheck size={16} />}>
            Residual Risk Summary
          </Tabs.Tab>
          <Tabs.Tab value="progress" leftSection={<IconChartBar size={16} />}>
            Area Progress
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="residual" pt="lg">
          <ResidualRiskCards
            riskFormData={riskFormData}
            residualRiskState={residualRiskData}
            savedControls={savedControls}
          />
        </Tabs.Panel>

        <Tabs.Panel value="progress" pt="lg">
          <Card withBorder radius="md" p="lg">
            <Stack gap="md">
              <Text fw={600} size="lg">Risk Area Assessment</Text>
              <Grid>
                {Object.entries(riskAnalysis.sectionBreakdown)
                  .filter(([_, data]) => data.total > 0)
                  .map(([section, data]) => {
                    const sectionRisks = risksWithResidual.filter(risk => risk.areaName === section);
                    const evaluatedRisks = sectionRisks.filter(risk => risk.residualData).length;
                    const totalActions = sectionRisks.reduce((sum, risk) => sum + risk.controls, 0);

                    const getStatusConfig = () => {
                      if (evaluatedRisks === data.total && data.total > 0) {
                        return {
                          color: 'green',
                          icon: '✅',
                          label: 'Complete',
                          bg: 'green.0'
                        };
                      } else if (evaluatedRisks > 0) {
                        return {
                          color: 'orange',
                          icon: '🔄',
                          label: 'Partial',
                          bg: 'orange.0'
                        };
                      } else {
                        return {
                          color: 'gray',
                          icon: '⏳',
                          label: 'Pending',
                          bg: 'gray.0'
                        };
                      }
                    };

                    const config = getStatusConfig();

                    return (
                      <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={section}>
                        <Card
                          withBorder
                          p="md"
                          bg={config.bg}
                          style={{
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <Stack gap="sm">
                            {/* Header */}
                            <Group justify="space-between" align="flex-start">
                              <div style={{ flex: 1 }}>
                                <Text fw={600} size="md" lineClamp={1}>
                                  {toTitleCase(section)}
                                </Text>
                                <Group gap="xs" mt={4}>
                                  <Text size="xs" c={config.color}>
                                    {config.icon} {config.label}
                                  </Text>
                                </Group>
                              </div>
                            </Group>

                            {/* Stats Grid */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr 1fr',
                              gap: '0.25rem',
                              marginTop: '0.5rem'
                            }}>
                              <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                                <Text size="sm" fw={600} c="blue">{Object.keys(allSectionRisks[section] || {}).length}</Text>
                                <Text size="xs" c="dimmed">Sources</Text>
                              </Box>
                              <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                                <Text size="sm" fw={600} c="purple">{data.total}</Text>
                                <Text size="xs" c="dimmed">Risks</Text>
                              </Box>
                              <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                                <Text size="sm" fw={600} c="green">{evaluatedRisks}</Text>
                                <Text size="xs" c="dimmed">Evaluated</Text>
                              </Box>
                              <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                                <Text size="sm" fw={600} c="orange">{totalActions}</Text>
                                <Text size="xs" c="dimmed">Actions</Text>
                              </Box>
                              <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                                <Text size="sm" fw={600} c="red">{data.Critical + data.High}</Text>
                                <Text size="xs" c="dimmed">High Risk</Text>
                              </Box>
                              <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                                <Progress
                                  value={data.total > 0 ? (evaluatedRisks / data.total) * 100 : 0}
                                  color={config.color}
                                  size="sm"
                                  radius="md"
                                />
                                <Text size="xs" c="dimmed">Progress</Text>
                              </Box>
                            </div>
                          </Stack>
                        </Card>
                      </Grid.Col>
                    );
                  })}
              </Grid>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}